import { Text } from "@carbon6/c6-frontend-design";
import { useEffect, useRef, type ReactNode } from "react";
import { hatcheryTextModes } from "@/app/theme";
import { BranchHashCell, type BranchHashParts } from "@/components/table/branchHash";
import { HatTextTooltip } from "@/components/tooltip";
import type { ConfigureSection, ConfigureSectionEntry } from "./configureSections";
import styles from "./ConfigureSectionNav.module.css";

// 구성 스텝(②)의 섹션 내비. 섹션마다 필요한 폭이 다르다(override 행 고정 폭, mux 토글 그룹,
// Policy 목록). 한 열에 세로로 쌓으면 좁은 섹션이 깨진다. 그래서 내비로 한 섹션만 열고, 상세
// 열이 자기 폭을 쓰게 한다. 내비가 전체 현황판이므로 요약과 status 를 항상 표시한다. 상세 열에는
// 머리글을 두지 않는다. 내비가 같은 이름과 요약을 이미 표시하므로 머리글은 중복이다.

// 상세 pane 은 한 자리에서 내용만 바뀌므로 패널 id 는 하나다. 선택된 탭은 aria-controls 로
// 같은 패널을 가리키고, 패널은 aria-labelledby 로 현재 탭을 가리킨다.
export const CONFIGURE_PANEL_ID = "training-submit-configure-panel";

export function sectionTabId(section: ConfigureSection): string {
  return `training-submit-configure-tab-${section}`;
}

// 섹션 목록. 이 컴포넌트는 이동을 소유하지 않는다. 클릭은 onSelect 로 들어오고, 키보드는 창
// 셸의 ↑/↓ 콜백으로 들어온다(`FullscreenWindow.onArrowUp`/`onArrowDown`). 그래서 표면마다
// 리스너와 가드를 다시 쓰지 않는다.
export function ConfigureSectionNav({
  entries,
  active,
  className,
  onSelect,
  codeSource,
}: {
  codeSource: BranchHashParts;
  entries: readonly ConfigureSectionEntry[];
  active: ConfigureSection;
  // 3열 격자에서의 자리(grid-row)는 스텝이 소유한다.
  className?: string;
  onSelect: (section: ConfigureSection) => void;
}): ReactNode {
  const navRef = useRef<HTMLDivElement | null>(null);
  // 키로 섹션을 옮겼을 때, 포커스가 이미 목록 안에 있을 때만 포커스를 옮긴다. 목록 밖에서 키를
  // 눌렀는데 포커스를 가져오면 사용자가 보던 자리가 사라진다.
  const activeTabId = sectionTabId(active);
  useEffect(() => {
    const nav = navRef.current;
    if (nav === null || !nav.contains(document.activeElement)) {
      return;
    }
    nav.querySelector<HTMLElement>(`#${CSS.escape(activeTabId)}`)?.focus();
  }, [activeTabId]);

  return (
    <div
      ref={navRef}
      className={className === undefined ? styles.nav : `${styles.nav} ${className}`}
      role="tablist"
      aria-orientation="vertical"
      aria-label="구성 섹션"
    >
      {entries.map((entry) => {
        const selected = entry.section === active;
        const lockedReason = entry.disabledReason;
        const locked = lockedReason !== null;
        const id = sectionTabId(entry.section);
        const tab = (
          <div
            key={entry.section}
            className={styles.item}
            data-active={selected ? "true" : "false"}
            data-disabled={locked || undefined}
          >
            {/* 복사 버튼은 탭 버튼과 형제다. 투명한 탭 버튼이 항목의 나머지 영역을 차지한다. */}
            <button
              type="button"
              role="tab"
              id={id}
              aria-labelledby={`${id}-label ${id}-summary`}
              aria-selected={selected}
              aria-controls={CONFIGURE_PANEL_ID}
              aria-disabled={locked || undefined}
              tabIndex={selected ? 0 : -1}
              className={styles.tab}
              onClick={locked ? undefined : () => onSelect(entry.section)}
            />
            <span className={styles.itemText}>
              <Text
                id={`${id}-label`}
                variant={hatcheryTextModes["caption-bold"]}
                className={styles.label}
              >
                {entry.label}
              </Text>
              <span id={`${id}-summary`} className={styles.summaries}>
                {entry.summaryAxes.map((axis) => (
                  <span key={axis.name} className={styles.summary}>
                    <span className={styles.summaryBullet} aria-hidden="true">
                      •
                    </span>
                    <span className={styles.summaryName}>{axis.name}</span>
                    <span className={styles.summaryText}>
                      {entry.section === "config" && axis.name === "Branch" ? (
                        <BranchHashCell {...codeSource} />
                      ) : (
                        axis.value
                      )}
                    </span>
                  </span>
                ))}
              </span>
            </span>
            {locked ? null : (
              <span className={styles.dot} data-status={entry.status} aria-hidden="true" />
            )}
          </div>
        );
        if (lockedReason === null) {
          return tab;
        }
        // 잠긴 이유는 hover 툴팁이 표시한다. 기본(text) mode 는 세로 목록에서 flex stretch 로
        // 전체 폭이 되고, 좁은 화면의 가로 스트립에서는 내용 폭으로 줄어든다.
        return (
          <HatTextTooltip key={entry.section} text={lockedReason} placement="right">
            {tab}
          </HatTextTooltip>
        );
      })}
    </div>
  );
}
